"use client";
import React, { useEffect } from "react";
import AOS from "aos";
import { ButtonCta, LiveChatButton } from "@/components";
import { fontSizes } from "@/constants/constants";

/* pull the real src whether an imported asset object or a string path is passed */
const srcOf = (img) => (typeof img === "string" ? img : img?.src || "");

const decoCovers = [
  "/images/book-covers/hero-slide-4.webp",
  "/images/book-covers/hero-slide-8.webp",
  "/images/book-covers/hero-slide-11.webp",
  "/images/book-covers/hero-slide-6.webp",
  "/images/book-covers/hero-slide-2.webp",
  "/images/Item-2.webp",
];

const hash = (s) => {
  s = String(s || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const VARIANTS = ["panel", "overlap", "showcase"];

function Buttons({ btnText, btnText2, btnStyle, dark }) {
  return (
    <div className="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start">
      <LiveChatButton
        buttonText={btnText}
        classStyle={dark ? `text-ink bg-white hover:bg-white/90 h-12 ${btnStyle || ""}` : `text-white bg-brand-600 hover:bg-brand-700 h-12 ${btnStyle || ""}`}
      />
      <ButtonCta
        buttonText={btnText2}
        classStyle={dark ? "text-white bg-transparent border border-white/40 hover:bg-white/10 h-12" : "text-ink bg-transparent border border-ink/15 hover:border-gold/60 hover:bg-white h-12"}
      />
    </div>
  );
}

function ImageContent({
  title,
  text1,
  image,
  reverse = false,
  btnText,
  btnStyle,
  btnText2,
  eyebrow,
  variant,
  classStyle = "",
  badgeValue = "500+",
  badgeLabel = "Titles Published",
}) {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const v = variant || VARIANTS[hash(typeof title === "string" ? title : text1) % 3];
  const img = srcOf(image);
  const h = hash(typeof title === "string" ? title : "x");
  const c1 = decoCovers[h % decoCovers.length];
  const c2 = decoCovers[(h + 2) % decoCovers.length];

  /* ─────────────── Variant 1: DARK FEATURE PANEL ─────────────── */
  if (v === "panel") {
    return (
      <section className="px-4 py-10">
        <div className={`relative mx-auto grid max-w-[1350px] items-center gap-10 overflow-hidden rounded-[32px] bg-gradient-to-br from-ink via-brand-900 to-ink p-6 md:grid-cols-2 md:p-12 ${classStyle}`}>
          <div aria-hidden className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-brand-500/25 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -right-12 -bottom-12 h-64 w-64 rounded-full bg-gold/15 blur-3xl" />

          <div data-aos={reverse ? "fade-left" : "fade-right"} className={`relative flex justify-center ${reverse ? "md:order-2" : ""}`}>
            <img loading="lazy" decoding="async" src={c2} alt="" aria-hidden className="absolute -left-4 top-6 z-0 hidden w-24 rotate-[-8deg] rounded-lg shadow-2xl ring-1 ring-white/10 md:block animate-float-slow" />
            <div className="relative z-10 overflow-hidden rounded-2xl shadow-editorial ring-1 ring-white/15">
              <img loading="lazy" decoding="async" src={img} alt={typeof title === "string" ? title : "feature"} className="w-full max-w-md object-cover" />
            </div>
            <img loading="lazy" decoding="async" src={c1} alt="" aria-hidden className="absolute -right-3 bottom-4 z-20 hidden w-20 rotate-[7deg] rounded-lg shadow-2xl ring-1 ring-white/10 md:block animate-float" />
          </div>

          <div data-aos={reverse ? "fade-right" : "fade-left"} className={`relative text-center md:text-left ${reverse ? "md:order-1" : ""}`}>
            <span className="eyebrow inline-flex items-center gap-3 text-gold-soft">
              <span className="h-px w-8 bg-gold/70" />
              {eyebrow || "Our Craft"}
            </span>
            <h2 className={`mt-4 font-display font-semibold text-white ${fontSizes.text_5_xl}`}>{title}</h2>
            <div className={`mt-5 leading-relaxed text-white/80 ${fontSizes.base}`}>{text1}</div>
            <Buttons btnText={btnText} btnText2={btnText2} btnStyle={btnStyle} dark />
          </div>
        </div>
      </section>
    );
  }

  /* ─────────────── Variant 2: MAGAZINE OVERLAP ─────────────── */
  if (v === "overlap") {
    return (
      <section className={`relative overflow-hidden bg-cream bg-paper-grain py-20 ${classStyle}`}>
        <div className="mx-auto max-w-6xl px-4 lg:flex lg:items-center">
          <div data-aos="zoom-in" className={`relative lg:w-[60%] ${reverse ? "lg:order-2" : ""}`}>
            <div className="overflow-hidden rounded-[2rem] shadow-editorial ring-1 ring-black/5">
              <img loading="lazy" decoding="async" src={img} alt={typeof title === "string" ? title : "feature"} className="h-[340px] w-full object-cover object-top md:h-[520px]" />
            </div>
            <img loading="lazy" decoding="async" src={c1} alt="" aria-hidden className="absolute -bottom-6 left-6 hidden w-28 rotate-[-6deg] rounded-xl shadow-2xl ring-1 ring-black/10 md:block animate-float-slow" />
          </div>

          <div data-aos="fade-up" className={`relative z-10 mx-auto -mt-12 max-w-xl rounded-[1.6rem] border border-ink/10 bg-white p-8 shadow-editorial md:p-10 lg:mt-0 lg:w-[46%] ${reverse ? "lg:order-1 lg:-mr-[5%]" : "lg:-ml-[5%]"}`}>
            <span className="eyebrow inline-flex items-center gap-3 text-gold-deep">
              <span className="h-px w-8 bg-gold/70" />
              {eyebrow || "The Craft"}
            </span>
            <h2 className={`mt-4 font-display font-semibold text-ink ${fontSizes.text_4_xl}`}>{title}</h2>
            <div className={`mt-4 leading-relaxed text-ink-soft ${fontSizes.base}`}>{text1}</div>
            <Buttons btnText={btnText} btnText2={btnText2} btnStyle={btnStyle} />
          </div>
        </div>
      </section>
    );
  }

  /* ─────────────── Variant 3: COLLAGE SHOWCASE ─────────────── */
  return (
    <section className={`relative overflow-hidden bg-paper py-20 ${classStyle}`}>
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-4 lg:grid-cols-2 lg:gap-8">
        <div data-aos="fade-up" className={`relative ${reverse ? "lg:order-2" : ""}`}>
          {/* stacked collage */}
          <div className="relative mx-auto h-[440px] max-w-md">
            <span aria-hidden className="absolute inset-6 rounded-[2rem] bg-gradient-to-br from-brand-200/50 to-gold-soft/30" />
            <img loading="lazy" decoding="async" src={c2} alt="" aria-hidden className="absolute left-0 top-2 w-40 rotate-[-9deg] rounded-xl shadow-2xl ring-1 ring-black/10 animate-float-slow" />
            <img loading="lazy" decoding="async" src={img} alt={typeof title === "string" ? title : "feature"} className="absolute left-1/2 top-8 w-52 -translate-x-1/2 rounded-xl object-cover shadow-editorial ring-1 ring-black/10" />
            <img loading="lazy" decoding="async" src={c1} alt="" aria-hidden className="absolute bottom-0 right-2 w-36 rotate-[8deg] rounded-xl shadow-2xl ring-1 ring-black/10 animate-float" />
            <div className="absolute bottom-6 left-2 z-20 rounded-2xl bg-white px-5 py-3 shadow-editorial ring-1 ring-black/5">
              <p className="font-display text-2xl font-semibold text-brand-700">{badgeValue}</p>
              <p className="text-[10px] uppercase tracking-widest text-ink-soft">{badgeLabel}</p>
            </div>
          </div>
        </div>

        <div data-aos="fade-up" data-aos-delay="100" className={`text-center lg:text-left ${reverse ? "lg:order-1" : ""}`}>
          <span className="eyebrow inline-flex items-center gap-3 text-gold-deep">
            <span className="h-px w-8 bg-gold/70" />
            {eyebrow || "Why It Works"}
          </span>
          <h2 className={`mt-4 font-display font-semibold text-ink ${fontSizes.text_5_xl}`}>{title}</h2>
          <div className={`mt-5 leading-relaxed text-ink-soft ${fontSizes.base}`}>{text1}</div>
          <Buttons btnText={btnText} btnText2={btnText2} btnStyle={btnStyle} />
        </div>
      </div>
    </section>
  );
}

export default ImageContent;
